import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Collapse,
  Avatar,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FiberManualRecord as DotIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const drawerWidth = 240;

const Sidebar = ({
  lang,
  theme,
  isMobile,
  selectedMenu,
  openSubmenus,
  mobileOpen,
  handleDrawerToggle,
  handleMenuClick,
  handleSubmenuClick,
  menuItems,
  web,
}) => {
  const drawer = (
    <Box
      sx={{
        height: "100%",
        backgroundColor: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Logo Section ── */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {web?.site_logo ? (
            <Box
              component="img"
              src={`/media/${web.site_logo}`}
              alt={web?.site_name || "Logo"}
              sx={{
                height: 40,
                maxWidth: 130,
                objectFit: "contain",
              }}
            />
          ) : (
            <>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "primary.main",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {web?.site_name?.charAt(0) || "A"}
              </Avatar>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                noWrap
                sx={{ maxWidth: 130 }}
              >
                {web?.site_name || "App"}
              </Typography>
            </>
          )}
        </Box>

        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* ── Navigation ── */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List sx={{ px: 1, py: 0 }}>
          {menuItems.map((item) => {
            const isParentActive =
              selectedMenu === item.id ||
              item.submenu?.some((sub) => sub.id === selectedMenu);

            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={isParentActive}
                    onClick={() => handleMenuClick(item.id, !!item.submenu)}
                    sx={{
                      borderRadius: 1.5,
                      py: 1.2,
                      px: 2,
                      minHeight: 44,
                      "&.Mui-selected": {
                        backgroundColor: `${theme.palette.primary.main}18`,
                        color: theme.palette.primary.main,
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.main,
                        },
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isParentActive
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        minWidth: 36,
                        "& svg": { fontSize: 20 },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isParentActive ? 600 : 500,
                      }}
                    />
                    {item.submenu &&
                      (openSubmenus[item.id] ? (
                        <ExpandLessIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      ))}
                  </ListItemButton>
                </ListItem>

                {/* Submenu */}
                {item.submenu && (
                  <Collapse
                    in={openSubmenus[item.id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem) => (
                        <ListItem
                          key={subItem.id}
                          disablePadding
                          sx={{ mb: 0.5 }}
                        >
                          <ListItemButton
                            selected={selectedMenu === subItem.id}
                            onClick={() => handleSubmenuClick(subItem.id)}
                            sx={{
                              borderRadius: 1.5,
                              py: 1,
                              pl: 6,
                              minHeight: 40,
                              "&.Mui-selected": {
                                backgroundColor: `${theme.palette.primary.main}12`,
                                color: theme.palette.primary.main,
                              },
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <DotIcon
                              sx={{
                                fontSize: 7,
                                mr: 1.5,
                                color:
                                  selectedMenu === subItem.id
                                    ? theme.palette.primary.main
                                    : theme.palette.text.disabled,
                              }}
                            />
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight:
                                  selectedMenu === subItem.id ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            border: "none",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            border: "none",
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
